import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Eye,
  Mail,
  Phone,
  Search,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("es-AR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Consultas() {
  const [q, setQ] = useState("");
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0 });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (q.trim()) params.set("q", q.trim());
    return params.toString();
  }, [q, limit, offset]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads?${queryString}`);
      if (!res.ok) throw new Error("bad_response");
      const json = await res.json();
      setData(json);
    } catch {
      toast.error(
        "No pudimos cargar las consultas. Verificá que el backend esté activo.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const deleteLead = async (id) => {
    try {
      const res = await fetch(`${API}/leads/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("bad_response");
      toast.success("Consulta eliminada.");
      fetchLeads();
    } catch {
      toast.error("No pudimos eliminar la consulta.");
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + limit < (data.total || 0);

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-border/70 bg-transparent hover:bg-accent"
              >
                <Link to="/">
                  <ArrowLeft className="mr-2" />
                  Volver a la landing
                </Link>
              </Button>
              <Badge className="rounded-full bg-card text-muted-foreground">
                Interno
              </Badge>
            </div>

            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-4xl">
              Consultas
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Panel simple para revisar consultas recibidas desde el formulario.
              (Sin login por ahora)
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 md:w-[420px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => {
                  setOffset(0);
                  setQ(e.target.value);
                }}
                placeholder="Buscar por empresa, email, mensaje…"
                className="pl-9 bg-background/40"
              />
            </div>
            <div className="flex items-center gap-3 md:justify-end">
              <Button
                variant="outline"
                className="rounded-full border-border/70 bg-white/40 hover:bg-secondary"
                onClick={() => fetchLeads()}
              >
                Actualizar
              </Button>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        <Card className="at-glass">
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-base">Leads</CardTitle>
            <div className="text-xs text-muted-foreground">
              {loading ? "Cargando…" : `${data.total || 0} en total`}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Mensaje</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && (!data.items || data.items.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center">
                      <div className="text-sm font-medium">Sin resultados</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Probá otra búsqueda o revisá más tarde.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : null}

                {(data.items || []).map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {fmtDate(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-semibold">{lead.empresa}</div>
                      <div className="text-xs text-muted-foreground">
                        {lead.nombre}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <a
                          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                          href={`mailto:${lead.email}`}
                        >
                          <Mail className="h-3.5 w-3.5" />
                          {lead.email}
                        </a>
                        <a
                          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                          href={`tel:${String(lead.telefono || "").replace(/\s/g, "")}`}
                        >
                          <Phone className="h-3.5 w-3.5" />
                          {lead.telefono}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[520px]">
                      <div className="line-clamp-2 text-sm text-foreground/90">
                        {lead.mensaje}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="h-9 rounded-full border-border/70 bg-transparent px-3 hover:bg-accent"
                            >
                              <Eye className="mr-2" />
                              Ver
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="at-glass max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="text-base">
                                {lead.empresa}
                              </DialogTitle>
                            </DialogHeader>

                            <div className="grid gap-3">
                              <div className="grid gap-2 rounded-xl border border-border/60 bg-card/30 p-4">
                                <div className="text-xs text-muted-foreground">
                                  {fmtDate(lead.created_at)}
                                </div>
                                <div className="text-sm">
                                  <span className="font-semibold">Contacto:</span>{" "}
                                  {lead.nombre}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {lead.email} • {lead.telefono}
                                </div>
                              </div>

                              <div className="rounded-xl border border-border/60 bg-card/30 p-4">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                  Mensaje
                                </div>
                                <div className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">
                                  {lead.mensaje}
                                </div>
                              </div>

                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <Button
                                  asChild
                                  variant="outline"
                                  className="rounded-full border-border/70 bg-transparent hover:bg-accent"
                                >
                                  <a
                                    href={`mailto:${lead.email}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Responder por email
                                    <ExternalLink className="ml-2" />
                                  </a>
                                </Button>
                                <Button
                                  variant="destructive"
                                  className="rounded-full"
                                  onClick={() => deleteLead(lead.id)}
                                >
                                  <Trash2 className="mr-2" />
                                  Eliminar
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <Button
                          variant="destructive"
                          className="h-9 rounded-full px-3"
                          onClick={() => deleteLead(lead.id)}
                        >
                          <Trash2 className="mr-2" />
                          Borrar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Separator className="my-6" />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-muted-foreground">
                Página: {Math.floor(offset / limit) + 1} (mostrando {limit} por
                página)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="rounded-full border-border/70 bg-transparent hover:bg-accent"
                  disabled={!canPrev}
                  onClick={() => setOffset((p) => Math.max(0, p - limit))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-border/70 bg-transparent hover:bg-accent"
                  disabled={!canNext}
                  onClick={() => setOffset((p) => p + limit)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
